import { getDocs, query, collection, where, orderBy, deleteDoc, doc, getDoc, writeBatch } from "firebase/firestore";
import { db } from "./firebase";
import { evaluationToFirestore, evaluationFromFirestore } from "../models/evaluation/evaluationConverter";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "./collections";

const EvaluationService = {
    getAllByMember: async (idTenant, idMember) => {
        if (!idTenant || !idMember) return [];
        const colRef = collection(db, "tenants", idTenant, COLLECTIONS.EVALUATIONS);
        const q = query(
            colRef,
            where("idMember", "==", idMember),
            orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(evaluationFromFirestore);
    },

    getById: async (idTenant, id) => {
        if (!idTenant || !id) throw new Error("Parâmetros inválidos");
        const docRef = doc(db, "tenants", idTenant, COLLECTIONS.EVALUATIONS, id);
        const snapshot = await getDoc(docRef);
        return evaluationFromFirestore(snapshot);
    },

    // Serve para ver todas avaliações que aconteceram em um determinado "Evento/Seminário"
    getAllByEvent: async (idTenant, idEvent) => {
        if (!idTenant || !idEvent) return [];
        const colRef = collection(db, "tenants", idTenant, COLLECTIONS.EVALUATIONS);
        const q = query(
            colRef,
            where("idEvent", "==", idEvent),
            orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(evaluationFromFirestore);
    },

    /**
     * Busca avaliações de uma atividade em um evento específico (ciclo de avaliação).
     */
    getByActivityAndEvent: async (idTenant, idActivity, idEvent) => {
        if (!idTenant || !idActivity || !idEvent) return [];
        const colRef = collection(db, "tenants", idTenant, COLLECTIONS.EVALUATIONS);
        const q = query(
            colRef,
            where("idActivity", "==", idActivity),
            where("idEvent", "==", idEvent)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    },

    /**
     * Busca a avaliação mais recente de cada membro para uma atividade (histórico).
     * Retorna um Map { idMember: evalDoc }
     */
    getLatestByMemberAndActivity: async (idTenant, idActivity, idMembers) => {
        if (!idTenant || !idActivity || !idMembers?.length) return {};
        const colRef = collection(db, "tenants", idTenant, COLLECTIONS.EVALUATIONS);
        const q = query(
            colRef,
            where("idActivity", "==", idActivity),
            orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(q);
        const result = {};
        snapshot.docs.forEach(d => {
            const data = { id: d.id, ...d.data() };
            const mid = data.idMember || data.memberNumber;
            if (mid && idMembers.includes(mid) && !result[mid]) {
                result[mid] = data;
            }
        });
        return result;
    },

    /**
     * Registra uma avaliação completa (usado pelo useSaveEvaluations).
     * Delega para o save() existente usando o formato padrão.
     */
    registerEvaluation: async (idTenant, authUser, data) => {
        return EvaluationService.save(data, idTenant, authUser);
    },

    /**
     * Busca todas as avaliações de um membro (para gerar relatório de progresso).
     */
    getAllByMemberForActivity: async (idTenant, idMember, idActivity) => {
        if (!idTenant || !idMember) return [];
        const colRef = collection(db, "tenants", idTenant, COLLECTIONS.EVALUATIONS);
        const constraints = [where("idMember", "==", idMember)];
        if (idActivity) constraints.push(where("idActivity", "==", idActivity));
        constraints.push(orderBy("createdAt", "desc"));
        const q = query(colRef, ...constraints);
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    },

    /**
     * Salva uma avaliação no Firestore.
     * 
     * NOTA: A lógica de XP e Gamificação foi movida para o hook useSaveEvaluations
     * que controla as duas lógicas de entrega:
     *   1. XP por tópico (quando aluno atinge nível máximo em tópico com XP configurado)
     *   2. XP por passagem de nível (quando % + fundamentais são satisfeitos)
     */
    save: async (data, idTenant, authUser) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const colRef = collection(db, "tenants", idTenant, COLLECTIONS.EVALUATIONS);
        let docRef;

        if (data.id) {
            docRef = doc(colRef, data.id);
        } else {
            docRef = doc(colRef);
        }

        const payload = evaluationToFirestore(data, { id: idTenant }, authUser);

        if (payload.idMember && (!payload.memberEmail || !payload.memberPhone || !payload.memberPhotoUrl)) {
            const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, payload.idMember);
            const memberSnap = await getDoc(memberRef);
            if (memberSnap.exists()) {
                const mData = memberSnap.data();
                if (!payload.memberEmail && mData.email) payload.memberEmail = mData.email;
                if (!payload.memberPhone && mData.phone) payload.memberPhone = mData.phone;
                if (!payload.memberPhotoUrl && mData.photoUrl) payload.memberPhotoUrl = mData.photoUrl;
            }
        }

        if (payload.idEmployee && (!payload.employeeEmail || !payload.employeePhone || !payload.employeePhotoUrl)) {
            const empRef = doc(db, "tenants", idTenant, "employees", payload.idEmployee);
            const empSnap = await getDoc(empRef);
            if (empSnap.exists()) {
                const eData = empSnap.data();
                if (!payload.employeeEmail && eData.email) payload.employeeEmail = eData.email;
                if (!payload.employeePhone && eData.phone) payload.employeePhone = eData.phone;
                if (!payload.employeePhotoUrl && eData.photoUrl) payload.employeePhotoUrl = eData.photoUrl;
            }
        }

        if (payload.idTeacher && (!payload.teacherEmail || !payload.teacherPhone || !payload.teacherPhotoUrl)) {
            const teacherRef = doc(db, "tenants", idTenant, "employees", payload.idTeacher);
            const teacherSnap = await getDoc(teacherRef);
            if (teacherSnap.exists()) {
                const tData = teacherSnap.data();
                if (!payload.teacherEmail && tData.email) payload.teacherEmail = tData.email;
                if (!payload.teacherPhone && tData.phone) payload.teacherPhone = tData.phone;
                if (!payload.teacherPhotoUrl && tData.photoUrl) payload.teacherPhotoUrl = tData.photoUrl;
            }
        }

        const batch = writeBatch(db);

        // Salva a avaliação
        batch.set(docRef, payload, { merge: true });

        await batch.commit();

        // Auditoria
        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.EVALUATIONS,
            action: data.id ? AUDIT_ACTIONS.UPDATE : AUDIT_ACTIONS.CREATE,
            entityId: docRef.id,
            entityName: payload.memberName || payload.idMember || docRef.id,
            description: `${data.id ? 'Atualizou' : 'Criou'} avaliação para "${payload.memberName || 'aluno'}"`,
            details: {
                aluno: payload.memberName || "-",
                idAluno: payload.idMember || "-",
                pontuacao: payload.resultScore || 0,
                nivelAtingido: payload.achievedLevelName || payload.targetLevelName || "-",
                evento: payload.idEvent || "-"
            }
        });

        return docRef.id;
    },

    delete: async (id, idTenant, authUser, evaluationName = "") => {
        if (!idTenant || !id) throw new Error("Parâmetros obrigatórios ausentes.");
        const docRef = doc(db, "tenants", idTenant, COLLECTIONS.EVALUATIONS, id);
        await deleteDoc(docRef);

        // Auditoria
        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.EVALUATIONS,
            action: AUDIT_ACTIONS.DELETE,
            entityId: id,
            entityName: evaluationName || id,
            description: `Excluiu avaliação "${evaluationName || id}"`,
            details: { idAvaliacaoRemovida: id }
        });
    }
};

export default EvaluationService;
